import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/shadcn/card";
import { Button } from "@/components/shadcn/button";
import { Badge } from "@/components/shadcn/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/shadcn/table";
import { adminApi, adminFeatureFlagBindingsQueryOptions } from "@/api/admin/admin";
import { ASSIGNABLE_ROLES } from "@/api/admin/admin.types";

export function FeatureFlagRoleBindings() {
    const [pendingRole, setPendingRole] = useState<Record<number, string>>({});
    const queryClient = useQueryClient();

    const { data: flags, isLoading } = useQuery(adminFeatureFlagBindingsQueryOptions);

    const invalidateFlags = () => queryClient.invalidateQueries({ queryKey: ["admin", "feature-flags"] });

    const bindMutation = useMutation({
        mutationFn: ({ featureFlagId, roleName }: { featureFlagId: number; roleName: string }) =>
            adminApi.bindFeatureFlagRole(featureFlagId, roleName),
        onSuccess: () => {
            toast.success("Role bound to feature flag");
            invalidateFlags();
        },
        onError: () => toast.error("Failed to bind role"),
    });

    const unbindMutation = useMutation({
        mutationFn: ({ featureFlagId, roleName }: { featureFlagId: number; roleName: string }) =>
            adminApi.unbindFeatureFlagRole(featureFlagId, roleName),
        onSuccess: () => {
            toast.success("Role unbound from feature flag");
            invalidateFlags();
        },
        onError: () => toast.error("Failed to unbind role"),
    });

    return (
        <Card>
            <CardHeader>
                <CardTitle>Feature flags → roles</CardTitle>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Feature</TableHead>
                            <TableHead>Global</TableHead>
                            <TableHead>Bound roles</TableHead>
                            <TableHead>Bind role</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={4}>Loading...</TableCell>
                            </TableRow>
                        )}
                        {!isLoading && flags?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4}>No feature flags found.</TableCell>
                            </TableRow>
                        )}
                        {flags?.map((flag) => (
                            <TableRow key={flag.featureFlagId}>
                                <TableCell>{flag.featureName}</TableCell>
                                <TableCell>{flag.isEnabledGlobally ? "Yes" : "No"}</TableCell>
                                <TableCell>
                                    <div className="flex flex-wrap gap-1">
                                        {flag.boundRoles.length === 0 && (
                                            <span className="text-on-surface-variant">None</span>
                                        )}
                                        {flag.boundRoles.map((roleName) => (
                                            <Badge key={roleName} variant="secondary" className="gap-1.5">
                                                {roleName}
                                                <button
                                                    type="button"
                                                    className="opacity-60 hover:opacity-100 cursor-pointer"
                                                    disabled={unbindMutation.isPending}
                                                    onClick={() =>
                                                        unbindMutation.mutate({
                                                            featureFlagId: flag.featureFlagId,
                                                            roleName,
                                                        })
                                                    }
                                                >
                                                    ✕
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <Select
                                            value={pendingRole[flag.featureFlagId] ?? ""}
                                            onValueChange={(value) =>
                                                setPendingRole((prev) => ({ ...prev, [flag.featureFlagId]: value }))
                                            }
                                        >
                                            <SelectTrigger size="sm" className="w-40">
                                                <SelectValue placeholder="Role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {ASSIGNABLE_ROLES.filter((role) => !flag.boundRoles.includes(role)).map(
                                                    (role) => (
                                                        <SelectItem key={role} value={role}>
                                                            {role}
                                                        </SelectItem>
                                                    )
                                                )}
                                            </SelectContent>
                                        </Select>
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            disabled={!pendingRole[flag.featureFlagId] || bindMutation.isPending}
                                            onClick={() =>
                                                bindMutation.mutate({
                                                    featureFlagId: flag.featureFlagId,
                                                    roleName: pendingRole[flag.featureFlagId],
                                                })
                                            }
                                        >
                                            Bind
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}
